import {
  Button, Inline, InlineMessage, PageContent, PageHeader, ReportIcon, Spinner, Stack, Text, TextLink,
} from '@olytic/foundation';
import { ReportDashboard, type ReportDashboardTile, type ReportType } from '@olytic/foundation/reports';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useDocumentTitle } from '../documentTitle';
import { REPORTS_ROUTE } from '../navigation';
import { NotFoundPage } from '../NotFoundPage';
import {
  createLimiter, DASHBOARD_RUNS_AT_ONCE, DASHBOARD_TILE_LIMIT, dashboardContents, favoriteContents, type ReadyTile, runDashboardTile,
  updatedText,
} from './reportDashboard';
import { useReportTypes } from './reportSchema';
import { type SavedReport, setFavorite, useLibrary } from './reportStore';

/**
 * /reports/dashboard/:folderId (MOTHER-151): a saved-report folder and its sub-folders as
 * Foundation's ReportDashboard. With `favorites`, it is My Dashboard instead
 * (/reports/dashboard/favorites, MOTHER-293): the viewer's own favorite reports. Every tile loads its report and runs it on the server,
 * DASHBOARD_RUNS_AT_ONCE at a time; a failed one keeps the server's message and a Retry
 * that runs that tile alone. Refresh (MOTHER-298) reruns every tile the same way, failed ones
 * too, and the header says when the last tile finished. Refreshing is by hand only: each tile is
 * a server report run. On My Dashboard each tile has Remove From Dashboard (MOTHER-301), which
 * un-favorites its report for the viewer (the library's star call) and nothing else: the tile
 * leaves once the server agrees, and the library reloads so the next favorite can fill in. A
 * refused call leaves the tile and shows the server's message. A folder dashboard has no Remove:
 * it lists the folder's reports, not the viewer's choice. The fetcher is the app's authenticated
 * fetch (App.tsx).
 */

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

type TileRun = { state: 'loading' } | { state: 'error'; error: string } | ReadyTile;

const LOADING: TileRun = { state: 'loading' };
const errorText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.');

/**
 * Runs each report's tile, a few at a time, again on Retry, and all of them again on Refresh. A
 * new `scope` (another folder, or My Dashboard) starts over; within one, a report that joins the
 * list runs on its own, and those already run keep their results (MOTHER-301: removing a tile
 * from My Dashboard brings in the next favorite without running the others again). `updatedAt` is
 * when the last run finished with none left running.
 */
function useTileRuns(fetcher: Fetch, scope: string, reports: SavedReport[] | null, types: ReportType[] | null) {
  const [runs, setRuns] = useState<Record<string, TileRun>>({});
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  // The current scope's runner and the reports it has started; replaced when the scope changes.
  const runner = useRef<{ run: (id: string) => void; started: Set<string> } | null>(null);
  const [generation, setGeneration] = useState(0);
  const shown = useRef<SavedReport[]>([]);
  shown.current = reports ?? [];
  useEffect(() => {
    if (!types) return undefined;
    const controller = new AbortController();
    const limit = createLimiter(DASHBOARD_RUNS_AT_ONCE);
    // Each tile's latest run: an older one still answering never overwrites it.
    const latest = new Map<string, symbol>();
    let running = 0;
    const run = (id: string) => {
      const token = Symbol(id);
      latest.set(id, token);
      running += 1;
      setRuns((previous) => ({ ...previous, [id]: LOADING }));
      const finish = (next: TileRun) => {
        if (controller.signal.aborted) return;
        running -= 1;
        if (latest.get(id) === token) setRuns((previous) => ({ ...previous, [id]: next }));
        if (running === 0) setUpdatedAt(Date.now());
      };
      limit(() => runDashboardTile(fetcher, id, types, controller.signal)).then(
        finish,
        (err: unknown) => finish({ state: 'error', error: errorText(err) }),
      );
    };
    setRuns({});
    setUpdatedAt(null);
    runner.current = { run, started: new Set() };
    setGeneration((g) => g + 1);
    return () => {
      controller.abort();
      runner.current = null;
    };
  }, [scope, types, fetcher]);
  // The report ids are the key: a reloaded library with the same reports does not run them again.
  const key = reports?.map((r) => r.id).join('\n') ?? null;
  useEffect(() => {
    const current = runner.current;
    if (key === null || !current) return;
    const ids = key === '' ? [] : key.split('\n');
    // A report that left forgets its run, so it runs afresh if it comes back.
    for (const id of current.started) if (!ids.includes(id)) current.started.delete(id);
    for (const id of ids) {
      if (current.started.has(id)) continue;
      current.started.add(id);
      current.run(id);
    }
  }, [key, generation]);
  const retry = useCallback((id: string) => runner.current?.run(id), []);
  const refresh = useCallback(() => { for (const report of shown.current) runner.current?.run(report.id); }, []);
  return { runs, updatedAt, retry, refresh };
}

const MY_DASHBOARD_TITLE = 'My Dashboard';

export function ReportDashboardPage({ fetcher, favorites = false }: { fetcher: Fetch; favorites?: boolean }): JSX.Element {
  const { folderId = '' } = useParams();
  const navigate = useNavigate();
  const { types, error: typesError } = useReportTypes(fetcher);
  const { library, error: libraryError, reload } = useLibrary(fetcher);
  // Reports taken off My Dashboard that the library may still list as favorites until it reloads.
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const [removeError, setRemoveError] = useState<string | null>(null);
  const removing = useRef(new Set<string>());
  const contents = useMemo(() => {
    if (!library) return null;
    if (favorites) {
      return {
        title: MY_DASHBOARD_TITLE,
        emptyMessage: 'No favorite reports yet. Star a report in the Reports library to pin it here.',
        ...favoriteContents(library, removed),
      };
    }
    const folder = dashboardContents(library, folderId);
    return folder ? { title: folder.folder.name, emptyMessage: 'No reports in this folder.', ...folder } : null;
  }, [library, folderId, favorites, removed]);
  useDocumentTitle(contents?.title ?? (favorites ? MY_DASHBOARD_TITLE : 'Reports'));
  const scope = favorites ? 'favorites' : `folder:${folderId}`;
  const { runs, updatedAt, retry, refresh } = useTileRuns(fetcher, scope, contents?.reports ?? null, types);

  /** Un-favorites the tile's report; a second click while the first is out sends nothing. */
  const removeTile = async (id: string) => {
    if (removing.current.has(id)) return;
    removing.current.add(id);
    try {
      await setFavorite(fetcher, id, false);
    } catch (err) {
      setRemoveError(errorText(err));
      return;
    } finally {
      removing.current.delete(id);
    }
    setRemoveError(null);
    setRemoved((previous) => new Set(previous).add(id));
    await reload();
  };

  if (libraryError) {
    return (
      <PageContent as="section" width="wide">
        <InlineMessage tone="error" role="alert">{libraryError}</InlineMessage>
      </PageContent>
    );
  }
  if (!library) {
    return (
      <PageContent as="section" width="wide">
        <Spinner />
      </PageContent>
    );
  }
  // The library lists only the folders this person may see: any other id is not here for them.
  if (!contents) return <NotFoundPage />;

  const tiles: ReportDashboardTile[] = contents.reports.map((report) => ({
    id: report.id,
    title: report.name,
    description: report.description,
    href: `${REPORTS_ROUTE}/${encodeURIComponent(report.id)}`,
    ...(runs[report.id] ?? LOADING),
  }));
  const refreshing = tiles.some((t) => t.state === 'loading');

  return (
    <PageContent as="section" width="wide">
      <Stack>
        <PageHeader
          className="rounded-[var(--radius-lg)] border shadow-[var(--shadow-xs)]"
          breadcrumbs={(
            <TextLink asChild>
              <Link to={REPORTS_ROUTE}>Back to Reports</Link>
            </TextLink>
          )}
          icon={<ReportIcon />}
          eyebrow="Dashboard"
          title={contents.title}
          actions={tiles.length === 0 ? undefined : (
            <Inline gap="sm" align="center" wrap>
              <Button variant="neutral" disabled={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
              {updatedAt === null ? null : <Text as="span" size="sm" tone="muted">{updatedText(updatedAt)}</Text>}
            </Inline>
          )}
        />
        {typesError ? <InlineMessage tone="error" role="alert">{typesError}</InlineMessage> : null}
        {removeError ? <InlineMessage tone="error" role="alert">{removeError}</InlineMessage> : null}
        {contents.total > contents.reports.length ? (
          <InlineMessage tone="info">
            Showing {DASHBOARD_TILE_LIMIT} of {contents.total} reports.
          </InlineMessage>
        ) : null}
        <ReportDashboard
          title="Reports"
          tiles={tiles}
          onOpenTile={(tile) => { if (tile.href) navigate(tile.href); }}
          onRetryTile={(tile) => retry(tile.id)}
          onRemoveTile={favorites ? (tile) => { void removeTile(tile.id); } : undefined}
          emptyMessage={contents.emptyMessage}
        />
      </Stack>
    </PageContent>
  );
}
